import { Alert, Typography } from "@mui/material";
import { useCreateStoryMutation } from "Api/Mutations/Stories/useCreateStoryMutation";
import { useStoryPostUsageQuery } from "Api/Queries/Stories/useStoryPostUsageQuery";
import { useGetContextsQuery } from "Auth/Api/Queries/Context/useGetContextsQuery";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { StoryForm } from "Components/Stories/Management/StoryForm";
import { useNavigate } from "Navigation/useNavigate";
import {
  EMPTY_STORY_FORM,
  type StoryFormData,
  toStorySaveRequest,
} from "Schemas/Stories/storyFormSchema";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { isStoryPostLimitError } from "Utils/Stories/storyErrors";
import { appUrl } from "Utils/UrlUtils";

const RESOURCES = Resources.Stories.Management;

/** New story: the primary action saves AND publishes; the secondary keeps it as a draft. */
export const StoryCreatePage = () => {
  const { t } = useResource();
  const navigate = useNavigate();

  const { data: contexts } = useGetContextsQuery();
  const { data: usage } = useStoryPostUsageQuery(
    contexts?.activeIdentityUserPublicID,
  );
  const isLimitReached = usage != null && usage.used >= usage.limit;

  const {
    mutate: createStory,
    isPending,
    isError,
    error,
  } = useCreateStoryMutation({
    onSuccess: () => {
      toast.success(t(RESOURCES.Form.SaveSuccess));
      navigate(appUrl("admin-stories"));
    },
  });

  const submit = (data: StoryFormData, publish: boolean) =>
    createStory({ story: toStorySaveRequest(data), publish });

  return (
    <AuthenticatedLayout title={t(RESOURCES.Form.CreateTitle)}>
      <StyledPageWrapper $maxWidth="880px">
        <StoryForm
          defaultValues={EMPTY_STORY_FORM}
          isPending={isPending}
          submitLabel={t(RESOURCES.Form.SaveAndPublish)}
          isSubmitDisabled={isLimitReached}
          onSubmit={(data) => submit(data, true)}
          secondaryLabel={t(RESOURCES.Form.SaveDraft)}
          onSecondarySubmit={(data) => submit(data, false)}
          footer={
            <>
              {usage && (
                <Typography variant="body2" color="text.secondary">
                  {t(Resources.Stories.PostStory.UsageLabel, {
                    used: usage.used,
                    limit: usage.limit,
                  })}
                </Typography>
              )}
              {isError ? (
                <Alert severity="error">
                  {t(
                    isStoryPostLimitError(error)
                      ? Resources.Stories.PostStory.LimitExceeded
                      : RESOURCES.Form.SaveError,
                  )}
                </Alert>
              ) : (
                isLimitReached && (
                  <Alert severity="warning">
                    {t(Resources.Stories.PostStory.LimitExceeded)}
                  </Alert>
                )
              )}
            </>
          }
        />
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

export default StoryCreatePage;
